import type { Store } from "@reduxjs/toolkit";
import type { Appstore } from "./store";

const SHOW_LOADER = "SHOW_LOADER";
const HIDE_LOADER = "HIDE_LOADER";
const SHOW_BLOCKING_OVERLAY = "SHOW_BLOCKING_OVERLAY";
const HIDE_BLOCKING_OVERLAY = "HIDE_BLOCKING_OVERLAY";

/** Wait this long before showing the globe; invisible block covers this window only. */
const SLOW_LOAD_THRESHOLD_MS =
  Number(process.env.REACT_APP_GLOBAL_LOADER_DELAY_MS) || 2000;

/** Minimum time the globe stays visible once shown. */
const MIN_GLOBE_DISPLAY_MS = 400;

let pendingCount = 0;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let clickShieldVisible = false;
let globeVisible = false;
let globeShownAt: number | null = null;

function getStore(): Store<Appstore> {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require("./store").default;
}

function cancelScheduledShow(): void {
  if (showTimer !== null) {
    clearTimeout(showTimer);
    showTimer = null;
  }
}

function cancelScheduledHide(): void {
  if (hideTimer !== null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function showClickShield(): void {
  if (clickShieldVisible || globeVisible) {
    return;
  }

  clickShieldVisible = true;
  getStore().dispatch({ type: SHOW_BLOCKING_OVERLAY });
}

function hideClickShield(): void {
  if (!clickShieldVisible) {
    return;
  }

  clickShieldVisible = false;
  getStore().dispatch({ type: HIDE_BLOCKING_OVERLAY });
}

function hideGlobeNow(): void {
  cancelScheduledHide();
  if (!globeVisible) {
    return;
  }

  globeVisible = false;
  globeShownAt = null;
  getStore().dispatch({ type: HIDE_LOADER });
}

function scheduleHide(): void {
  cancelScheduledHide();
  if (!globeVisible || globeShownAt === null) {
    return;
  }

  const remaining = MIN_GLOBE_DISPLAY_MS - (Date.now() - globeShownAt);
  if (remaining <= 0) {
    hideGlobeNow();
    return;
  }

  hideTimer = setTimeout(() => {
    hideTimer = null;
    if (pendingCount === 0) {
      hideGlobeNow();
    }
  }, remaining);
}

function scheduleShow(): void {
  if (showTimer !== null || globeVisible) {
    return;
  }

  showTimer = setTimeout(() => {
    showTimer = null;
    if (pendingCount > 0) {
      hideClickShield();
      globeVisible = true;
      globeShownAt = Date.now();
      getStore().dispatch({ type: SHOW_LOADER });
    }
  }, SLOW_LOAD_THRESHOLD_MS);
}

export function requestGlobeLoader(): void {
  pendingCount++;
  if (pendingCount === 1) {
    showClickShield();
    scheduleShow();
  }
}

export function releaseGlobeLoader(): void {
  pendingCount = Math.max(0, pendingCount - 1);
  if (pendingCount > 0) {
    return;
  }

  cancelScheduledShow();

  if (globeVisible) {
    scheduleHide();
    return;
  }

  hideClickShield();
}

/** Clears pending state on route changes so interaction cannot stay blocked. */
export function resetGlobeLoader(): void {
  pendingCount = 0;
  cancelScheduledShow();
  cancelScheduledHide();
  hideClickShield();
  hideGlobeNow();
}
